/**
 * ProofQuest - Detective-themed proof construction interface
 * 
 * "You're not checking arithmetic. You're building an airtight case."
 * 
 * UI inspired by Phoenix Wright: Ace Attorney
 * - Evidence board (givens)
 * - Case file (proof steps)
 * - Rule book (available theorems)
 * - The Verdict (goal)
 */

import { useState, useCallback } from 'react';
import {
  PROOF_LEVEL_INFO,
  PROOF_DOMAIN_INFO,
  getProofProblems,
  getRulesForProblem,
  validateStep,
  validateProof,
  type ProofProblem,
  type ProofStep,
  type ProofRule,
  type ProofLevel,
  type ProofDomain,
} from '../lib/proofs';
import { useVoice } from '../hooks/useVoice';
import { tracker } from '../lib/tracking';

// ============================================================================
// MAIN PROOF QUEST COMPONENT
// ============================================================================

interface ProofQuestProps {
  problem: ProofProblem;
  childId: string;
  onComplete: (result: { score: number; passed: boolean; xpEarned: number }) => void;
}

export function ProofQuest({ problem, childId, onComplete }: ProofQuestProps) {
  // State
  const [steps, setSteps] = useState<Array<{
    statement: string;
    reason: string;
    validated?: boolean;
    feedback?: string;
  }>>([]);
  const [currentStatement, setCurrentStatement] = useState('');
  const [currentReason, setCurrentReason] = useState('');
  const [showRules, setShowRules] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [hintIndex, setHintIndex] = useState(0);
  const [completed, setCompleted] = useState(false);
  const [finalResult, setFinalResult] = useState<{
    valid: boolean;
    score: number;
    feedback: string;
  } | null>(null);
  
  const voice = useVoice({ persona: 'coach' });
  const availableRules = getRulesForProblem(problem.id);
  const domainInfo = PROOF_DOMAIN_INFO[problem.domain];
  const levelInfo = PROOF_LEVEL_INFO[problem.level];
  
  // Add a step to the proof
  const addStep = useCallback(() => {
    if (!currentStatement.trim() || !currentReason.trim()) return;
    
    const newStep = {
      statement: currentStatement.trim(),
      reason: currentReason.trim(),
    };
    
    // Validate against previous steps
    const validatedSteps = steps.filter(s => s.validated);
    const result = validateStep(newStep, problem, validatedSteps as ProofStep[]);
    
    setSteps(prev => [...prev, {
      ...newStep,
      validated: result.valid,
      feedback: result.feedback,
    }]);
    
    // Announce result
    if (result.valid) {
      voice.speak('Nice work! That step checks out.');
    } else {
      voice.speak('Hmm, let\'s think about that one again.');
    }
    
    setCurrentStatement('');
    setCurrentReason('');
    
    // Track attempt
    tracker.trackChallenge({
      challengeId: problem.id,
      templateId: `proof_${problem.domain}`,
      skillTag: `proof.${problem.domain}.${problem.level}`,
      action: 'submit',
      score: result.valid ? 1 : 0,
      userId: childId,
      isChild: true,
    });
  }, [currentStatement, currentReason, steps, problem, childId]);
  
  // Submit final proof
  const submitProof = useCallback(() => {
    const result = validateProof(
      { odId: problem.id, steps },
      problem
    );
    
    setFinalResult(result);
    setCompleted(true);
    
    const xpEarned = Math.round(problem.xpReward * result.score);
    
    if (result.valid) {
      voice.speak('Case closed! Your proof is airtight!');
    } else if (result.score > 0.5) {
      voice.speak('Good progress, but the case isn\'t complete yet.');
    }
    
    tracker.trackChallenge({
      challengeId: problem.id,
      templateId: `proof_${problem.domain}`,
      skillTag: `proof.${problem.domain}.${problem.level}`,
      action: 'complete',
      score: result.score,
      passed: result.valid,
      userId: childId,
      isChild: true,
    });
    
    setTimeout(() => {
      onComplete({
        score: result.score,
        passed: result.valid,
        xpEarned,
      });
    }, 3000);
  }, [steps, problem, childId, onComplete]);
  
  // Use a rule from the rulebook
  const selectRule = (rule: ProofRule) => {
    setCurrentReason(rule.shortName);
    setShowRules(false);
  };
  
  // Get next hint
  const getHint = () => {
    if (hintIndex < problem.hints.length) {
      voice.speak(problem.hints[hintIndex]);
      setShowHint(true);
      setHintIndex(prev => Math.min(prev + 1, problem.hints.length - 1));
    }
  };
  
  return (
    <div className="min-h-screen bg-blox-darker p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <span className="text-4xl">{domainInfo.emoji}</span>
            <div>
              <h1 className="font-display text-2xl text-white">{problem.title}</h1>
              <div className="flex items-center gap-2 mt-1">
                <span 
                  className="px-2 py-0.5 rounded text-xs font-semibold"
                  style={{ backgroundColor: `${domainInfo.color}33`, color: domainInfo.color }}
                >
                  {domainInfo.name}
                </span>
                <span className="px-2 py-0.5 rounded bg-blox-surface text-slate-300 text-xs">
                  {levelInfo.name}
                </span>
              </div>
            </div>
          </div>
          
          <div className="text-right">
            <p className="text-2xl font-display text-blox-xp">+{problem.xpReward}</p>
            <p className="text-xs text-slate-400">XP Reward</p>
          </div>
        </div>
        
        {/* Scenario */}
        <div className="mb-6 p-4 rounded-2xl bg-blox-card border border-blox-surface">
          <p className="text-sm text-slate-300 whitespace-pre-line font-mono">
            {problem.scenario}
          </p>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left column: Evidence Board */}
          <div className="space-y-4">
            {/* Givens */}
            <div className="rounded-2xl bg-blox-surface border border-blox-card p-4">
              <h2 className="font-display text-sm text-blox-accent uppercase tracking-wider mb-3 flex items-center gap-2">
                <span>📋</span> Evidence Board
              </h2>
              <div className="space-y-2">
                {problem.givens.map((given, i) => (
                  <div 
                    key={given.id}
                    className="p-3 rounded-xl bg-blox-dark border border-blox-surface"
                  >
                    <p className="text-xs text-slate-500 mb-1">{given.label || `Evidence ${i + 1}`}</p>
                    <p className="text-sm text-white font-mono">{given.statement}</p>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Goal */}
            <div className="rounded-2xl bg-blox-gold/10 border border-blox-gold/30 p-4">
              <h2 className="font-display text-sm text-blox-gold uppercase tracking-wider mb-3 flex items-center gap-2">
                <span>🎯</span> {problem.goal.label || 'Prove This'}
              </h2>
              <p className="text-lg text-white font-mono">{problem.goal.statement}</p>
            </div>
            
            {/* Hint */}
            {showHint && hintIndex > 0 && (
              <div className="rounded-2xl bg-blox-rare/10 border border-blox-rare/30 p-4 animate-slide-up">
                <h3 className="text-xs text-blox-rare uppercase tracking-wider mb-2">💡 Hint</h3>
                <p className="text-sm text-slate-300">{problem.hints[hintIndex - 1]}</p>
              </div>
            )}
          </div>
          
          {/* Middle column: Case File (Proof) */}
          <div className="lg:col-span-2">
            <div className="rounded-2xl bg-blox-card border border-blox-surface p-4">
              <h2 className="font-display text-sm text-white uppercase tracking-wider mb-4 flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <span>📝</span> Case File (Your Proof)
                </span>
                <span className="text-xs text-slate-500 font-normal">
                  {steps.filter(s => s.validated).length} / {problem.solution.length} steps
                </span>
              </h2>
              
              {/* Two-column proof table */}
              <div className="rounded-xl overflow-hidden border border-blox-surface mb-4">
                {/* Header */}
                <div className="grid grid-cols-2 bg-blox-dark">
                  <div className="px-4 py-2 text-xs text-slate-400 uppercase tracking-wider border-r border-blox-surface">
                    Statement
                  </div>
                  <div className="px-4 py-2 text-xs text-slate-400 uppercase tracking-wider">
                    Reason
                  </div>
                </div>
                
                {/* Existing steps */}
                {steps.map((step, i) => (
                  <div 
                    key={i}
                    className={`grid grid-cols-2 border-t border-blox-surface ${
                      step.validated 
                        ? 'bg-blox-xp/5' 
                        : 'bg-red-500/5'
                    }`}
                  >
                    <div className="px-4 py-3 border-r border-blox-surface">
                      <p className="text-sm text-white font-mono">{step.statement}</p>
                      {step.feedback && (
                        <p className={`text-xs mt-1 ${step.validated ? 'text-blox-xp' : 'text-red-400'}`}>
                          {step.feedback}
                        </p>
                      )}
                    </div>
                    <div className="px-4 py-3">
                      <p className="text-sm text-slate-300">{step.reason}</p>
                    </div>
                  </div>
                ))}
                
                {/* Input row */}
                {!completed && (
                  <div className="grid grid-cols-2 border-t border-blox-surface bg-blox-dark/50">
                    <div className="px-2 py-2 border-r border-blox-surface">
                      <input
                        type="text"
                        value={currentStatement}
                        onChange={(e) => setCurrentStatement(e.target.value)}
                        placeholder="Enter statement..."
                        className="w-full px-3 py-2 rounded-lg bg-blox-surface border border-blox-card text-white text-sm font-mono placeholder-slate-500 focus:outline-none focus:border-blox-accent"
                      />
                    </div>
                    <div className="px-2 py-2 flex gap-2">
                      <input
                        type="text"
                        value={currentReason}
                        onChange={(e) => setCurrentReason(e.target.value)}
                        placeholder="Select or type reason..."
                        className="flex-1 px-3 py-2 rounded-lg bg-blox-surface border border-blox-card text-white text-sm placeholder-slate-500 focus:outline-none focus:border-blox-accent"
                      />
                      <button
                        onClick={() => setShowRules(!showRules)}
                        className="px-3 py-2 rounded-lg bg-blox-surface border border-blox-card text-slate-300 hover:bg-blox-card transition-colors"
                        title="Open Rule Book"
                      >
                        📖
                      </button>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Action buttons */}
              {!completed && (
                <div className="flex items-center justify-between">
                  <div className="flex gap-2">
                    <button
                      onClick={getHint}
                      className="px-4 py-2 rounded-xl text-sm bg-blox-rare/20 text-blox-rare border border-blox-rare/30 hover:bg-blox-rare/30 transition-colors"
                    >
                      💡 Hint ({problem.hints.length - hintIndex} left)
                    </button>
                  </div>
                  
                  <div className="flex gap-2">
                    <button
                      onClick={addStep}
                      disabled={!currentStatement.trim() || !currentReason.trim()}
                      className="px-4 py-2 rounded-xl text-sm bg-blox-surface text-white hover:bg-blox-card transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      + Add Step
                    </button>
                    <button
                      onClick={submitProof}
                      disabled={steps.length === 0}
                      className="px-6 py-2 rounded-xl text-sm bg-blox-accent text-blox-dark font-semibold hover:bg-blox-accent/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      🔨 Close Case
                    </button>
                  </div>
                </div>
              )}
              
              {/* Completion state */}
              {completed && finalResult && (
                <div className={`p-4 rounded-xl ${
                  finalResult.valid 
                    ? 'bg-blox-xp/10 border border-blox-xp/30' 
                    : 'bg-orange-500/10 border border-orange-500/30'
                } animate-pop-in`}>
                  <div className="flex items-center gap-3">
                    <span className="text-4xl">{finalResult.valid ? '🎉' : '🤔'}</span>
                    <div>
                      <p className={`font-display text-lg ${finalResult.valid ? 'text-blox-xp' : 'text-orange-400'}`}>
                        {finalResult.valid ? 'Case Closed!' : 'Investigation Continues...'}
                      </p>
                      <p className="text-sm text-slate-300">{finalResult.feedback}</p>
                      <p className="text-sm text-slate-400 mt-1">
                        Score: {Math.round(finalResult.score * 100)}% | 
                        XP Earned: +{Math.round(problem.xpReward * finalResult.score)}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Rule Book Modal */}
        {showRules && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-blox-card rounded-2xl border border-blox-surface max-w-2xl w-full max-h-[80vh] overflow-hidden animate-pop-in">
              <div className="p-4 border-b border-blox-surface flex items-center justify-between">
                <h2 className="font-display text-lg text-white flex items-center gap-2">
                  <span>📖</span> Rule Book
                </h2>
                <button
                  onClick={() => setShowRules(false)}
                  className="p-2 rounded-lg hover:bg-blox-surface transition-colors"
                >
                  ✕
                </button>
              </div>
              
              <div className="p-4 overflow-y-auto max-h-[60vh]">
                <p className="text-sm text-slate-400 mb-4">
                  Click a rule to use it as your reason
                </p>
                
                {/* Group by category */}
                {['property', 'postulate', 'theorem', 'definition'].map(category => {
                  const rules = availableRules.filter(r => r.category === category);
                  if (rules.length === 0) return null;
                  
                  return (
                    <div key={category} className="mb-4">
                      <h3 className="text-xs text-slate-500 uppercase tracking-wider mb-2">
                        {category === 'property' ? '⚖️ Properties' :
                         category === 'postulate' ? '📍 Postulates' :
                         category === 'theorem' ? '📜 Theorems' :
                         '📚 Definitions'}
                      </h3>
                      <div className="space-y-2">
                        {rules.map(rule => (
                          <button
                            key={rule.id}
                            onClick={() => selectRule(rule)}
                            className="w-full text-left p-3 rounded-xl bg-blox-surface hover:bg-blox-dark border border-blox-card hover:border-blox-accent transition-all"
                          >
                            <p className="font-semibold text-white text-sm">{rule.name}</p>
                            <p className="text-xs text-slate-400 mt-1">{rule.statement}</p>
                            {rule.example && (
                              <p className="text-xs text-blox-accent mt-1 font-mono">
                                Example: {rule.example}
                              </p>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// PROOF QUEST SELECTOR
// ============================================================================

interface ProofQuestSelectorProps {
  onSelectProblem: (problem: ProofProblem) => void;
  completedProblems?: string[];
}

export function ProofQuestSelector({ onSelectProblem, completedProblems = [] }: ProofQuestSelectorProps) {
  const [selectedDomain, setSelectedDomain] = useState<ProofDomain | 'all'>('all');
  const [selectedLevel, setSelectedLevel] = useState<ProofLevel | 'all'>('all');
  
  const problems = getProofProblems({
    domain: selectedDomain === 'all' ? undefined : selectedDomain,
    level: selectedLevel === 'all' ? undefined : selectedLevel,
  });
  
  const domains: Array<ProofDomain | 'all'> = ['all', 'logic', 'algebra', 'geometry', 'number_theory'];
  const levels: Array<ProofLevel | 'all'> = ['all', 'intro', 'guided', 'scaffolded', 'independent', 'mastery'];
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h1 className="font-display text-3xl text-white mb-2">
          🔍 PROOF <span className="text-blox-accent">QUEST</span>
        </h1>
        <p className="text-slate-400">
          Build airtight cases. Every step must follow logically.
        </p>
      </div>
      
      {/* Filters */}
      <div className="flex flex-wrap gap-4 justify-center">
        <div>
          <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Domain</p>
          <div className="flex gap-1">
            {domains.map(domain => (
              <button
                key={domain}
                onClick={() => setSelectedDomain(domain)}
                className={`px-3 py-1.5 rounded-lg text-sm transition-all ${
                  selectedDomain === domain
                    ? 'bg-blox-accent text-blox-dark font-semibold'
                    : 'bg-blox-surface text-slate-300 hover:bg-blox-card'
                }`}
              >
                {domain === 'all' ? 'All' : PROOF_DOMAIN_INFO[domain].emoji}
              </button>
            ))}
          </div>
        </div>
        
        <div>
          <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Difficulty</p>
          <div className="flex gap-1">
            {levels.map(level => (
              <button
                key={level}
                onClick={() => setSelectedLevel(level)}
                className={`px-3 py-1.5 rounded-lg text-sm transition-all ${
                  selectedLevel === level
                    ? 'bg-blox-accent text-blox-dark font-semibold'
                    : 'bg-blox-surface text-slate-300 hover:bg-blox-card'
                }`}
              >
                {level === 'all' ? 'All' : PROOF_LEVEL_INFO[level].name.split(' ')[0]}
              </button>
            ))}
          </div>
        </div>
      </div>
      
      {/* Problem grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {problems.map(problem => {
          const isCompleted = completedProblems.includes(problem.id);
          const domainInfo = PROOF_DOMAIN_INFO[problem.domain];
          const levelInfo = PROOF_LEVEL_INFO[problem.level];
          
          return (
            <button
              key={problem.id}
              onClick={() => !isCompleted && onSelectProblem(problem)}
              disabled={isCompleted}
              className={`text-left p-4 rounded-2xl border transition-all hover:scale-[1.02] ${
                isCompleted
                  ? 'bg-blox-xp/10 border-blox-xp/30 opacity-70 cursor-default'
                  : 'bg-blox-card border-blox-surface hover:border-blox-accent cursor-pointer'
              }`}
            >
              <div className="flex items-start justify-between mb-2">
                <span className="text-2xl">{domainInfo.emoji}</span>
                {isCompleted && <span className="text-blox-xp">✓</span>}
              </div>
              
              <h3 className="font-display text-lg text-white mb-1">{problem.title}</h3>
              
              <div className="flex items-center gap-2 mb-3">
                <span 
                  className="px-2 py-0.5 rounded text-xs"
                  style={{ backgroundColor: `${domainInfo.color}22`, color: domainInfo.color }}
                >
                  {domainInfo.name}
                </span>
                <span className="px-2 py-0.5 rounded bg-blox-surface text-slate-400 text-xs">
                  {levelInfo.name}
                </span>
              </div>
              
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-500">
                  ~{problem.estimatedMinutes} min
                </span>
                <span className="text-blox-xp font-display">
                  +{problem.xpReward} XP
                </span>
              </div>
            </button>
          );
        })}
      </div>
      
      {problems.length === 0 && (
        <div className="text-center py-12 text-slate-400">
          <p className="text-4xl mb-4">🔎</p>
          <p>No cases match your filters.</p>
          <p className="text-sm">Try selecting different options!</p>
        </div>
      )}
    </div>
  );
}
